import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Toolbar,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { FullScreenDialogTransition } from '../../../Dialog/FullScreenDialogTransition';
import {
  OrganizationSchema,
  RoomCollaboratorSchema,
  UserSchema,
} from '../../../../definitions/schemas';
import { RoomType } from '../../../../enums/RoomTypes';
import AddCollaborators from './AddCollaboratorsDialog';
import RoomBussinessCard from '../RoomBusinessCard';
import CoBrandedLogo from '../../../CoBrandedLogo';
import LayoutContent from '../../../Layout/LayoutContent';
import Layout from '../../../Layout/Layout';
import { composeUsername } from '../../../../lib/DisplayName';

interface Props {
  clientLogo?: string;
  disableSaveOwner?: boolean;
  hideEmail?: boolean;
  onClose: () => void;
  onRoomOwnerChange?: (value: UserSchema) => void;
  onSuccess?: () => void;
  onUpdateProfile?: (user: UserSchema) => void;
  onUpdateRoleDescription?: (
    collaborator: RoomCollaboratorSchema,
    roleDescription: string
  ) => void;
  open: boolean;
  organization: OrganizationSchema;
  owner: UserSchema;
  readOnly?: boolean;
  referrer: string;
  roomCollaborators: RoomCollaboratorSchema[];
  roomUuid?: string;
}

const ListCollaboratorsModal: React.FC<Props> = (props: Props) => {
  const {
    clientLogo,
    disableSaveOwner,
    hideEmail = false,
    onClose,
    onRoomOwnerChange,
    onSuccess,
    onUpdateProfile,
    onUpdateRoleDescription,
    open,
    organization,
    owner,
    readOnly = false,
    referrer,
    roomCollaborators,
    roomUuid,
  } = props;

  const [openAddCollaborators, setOpenAddCollaborators] = useState(false);

  const renderCollaborators = () =>
    roomCollaborators.map((collaborator) => (
      <Grid item key={`collaborator-${collaborator.id}`} xs={12} sm={6} md={4}>
        <RoomBussinessCard
          cardBorder
          collaborator={collaborator}
          editable={!readOnly}
          editProfileButtonProps={{ fullWidth: true }}
          email={collaborator.email}
          enableBody
          enableHeader
          hideEmail={hideEmail}
          jobTitle={collaborator.jobTitle}
          linkedin={collaborator.linkedin}
          onRoomOwnerChange={onSuccess}
          onUpdateProfile={onUpdateProfile}
          onUpdateRoleDescription={(roleDescription) =>
            onUpdateRoleDescription &&
            onUpdateRoleDescription(collaborator, roleDescription)
          }
          owner={collaborator.collaboratorId === owner.id ? owner : undefined}
          phoneNumber={collaborator.phoneNumber}
          photo={collaborator.photo}
          readOnly={readOnly}
          roleDescription={collaborator.roleDescription}
          username={composeUsername(collaborator)}
        />
      </Grid>
    ));

  const renderManageCollaborators = () => {
    if (!(roomUuid && !readOnly)) {
      return null;
    }

    return (
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          color="secondary"
          onClick={() => setOpenAddCollaborators(true)}
          variant="contained"
        >
          Manage collaborators
        </Button>
        <AddCollaborators
          disableSaveOwner={disableSaveOwner}
          onClose={() => setOpenAddCollaborators(false)}
          onSaveSuccess={onSuccess}
          open={openAddCollaborators}
          owner={owner}
          referrer={referrer}
          roomType={RoomType.Deal}
          roomUuid={roomUuid}
          onRoomOwnerChange={onRoomOwnerChange}
        />
      </Box>
    );
  };

  return (
    <Dialog
      fullScreen
      onClose={onClose}
      open={open}
      TransitionComponent={FullScreenDialogTransition}
    >
      <Layout
        appBar={
          <AppBar color="transparent" position="static">
            <Toolbar>
              <Box alignItems="center" display="flex">
                <IconButton
                  aria-label="close"
                  color="inherit"
                  edge="start"
                  onClick={onClose}
                  size="large"
                >
                  <Close />
                </IconButton>
              </Box>
              <Box ml={-4.5} display="flex" flex={1} justifyContent="center">
                <CoBrandedLogo
                  clientLogoPreview={clientLogo}
                  organization={organization}
                />
              </Box>
            </Toolbar>
          </AppBar>
        }
      >
        <LayoutContent>
          <Container maxWidth="lg">
            {renderManageCollaborators()}
            <Grid container spacing={2}>
              {renderCollaborators()}
            </Grid>
          </Container>
        </LayoutContent>
      </Layout>
    </Dialog>
  );
};

export default ListCollaboratorsModal;
